import React, {useState, useEffect, useCallback, useRef} from 'react'

// Deps
import QRCode from 'qrcode'
import {openModal} from 'utils/modals'
import {getRoute} from 'controllers/navigator'
import config from 'config'
import {connect} from 'react-redux'

// Functions
import formatDate from 'functions/format-date';
import getCurrencySymbol from 'functions/get-currency-symbol';
import parseProducts from 'functions/parse-products'

// Partials
import {Table, TR, TD} from 'views/shared/partials/table'
import Btn from 'views/shared/partials/btn'
import Loader from 'views/shared/partials/loader'
import Tabs from 'views/shared/partials/tabs'
import Pricebox from 'views/shared/partials/pricebox'
import Image from 'views/shared/partials/image'
import ConversionInfo from 'views/shared/partials/conversion-info'
import OrderStatus from 'views/shared/partials/order-status'
import {FormInput} from 'views/shared/partials/forms'

// Services
import orderServices from 'services/order'

// Data
import notificationTypes from 'data/notification-types'
import orderStatusIndex from 'data/order-status-index'
import commerceStatusCodes from 'data/commerce-status-codes-detail'
import {useLocation} from "react-router-dom";

const mapStateToProps = state => {
    return {
        userData: state.user.data,
    };
};

const OrderSummary = ({onError, close, orderCode, className, onNotificationSent, mode, userData, ...props}) => {
    const [order, setOrder] = useState(false);
    const [orderPayments, setOrderPayments] = useState(false);
    const [refreshing, setRefreshing] = useState(false);
    const [urlCopied, setUrlCopied] = useState(false);
    const location = useLocation()

    // Product Order Info
    const [commerceStatus, setCommerceStatus] = useState(null);
    const [updatingCommerceStatus, setUpdatingCommerceStatus] = useState(false);

    const paymentUrlInput = useRef();

    const getOrderPayments = useCallback(() => {
        setOrderPayments(false);
        orderServices.listPayments({order_business_code: orderCode}).then((payload) => {
            setOrderPayments(payload.payments);
        });
    }, [orderCode]);

    const getOrder = useCallback(() => {
        setRefreshing(true);
        orderServices.getOrder(orderCode).then((payload) => {
            let qrImage = false;

            let paymentUrl = (config.environment === 'development' ? window.location.origin : payload.company.company_valid_url) + getRoute('payment', 'pages', 'payment').path.replace(':orderCode', payload.order_code);

            const total = parseFloat(payload.total_amount);
            let remainingPrice = total - (payload.payment_amount ? parseFloat(payload.payment_amount) : 0) + (payload.refund_amount ? parseFloat(payload.refund_amount) : 0);

            let paidPrice = total - remainingPrice;


            QRCode.toDataURL(paymentUrl, {
                margin: 0,
                color: {
                    dark: '#3e475f',
                    light: '#FFF'
                }
            }).then(image => {
                qrImage = image;
            }).catch(err => {
                console.error(err)
            }).finally(() => {
                setOrder({
                    ...payload,
                    qrImage: qrImage,
                    order_url: paymentUrl,
                    paid_price: paidPrice,
                    remaining_price: remainingPrice,
                    parsedProducts: parseProducts(payload.products),
                    statusObject: orderStatusIndex[payload.status.toString()],
                    addressDelivery: payload.product_delivery_method_id !== null ? payload.product_delivery_method_id === 2 : null,
                });

                const newStatus = payload.product_order_status ? commerceStatusCodes.find(code => code.value === payload.product_order_status.toString()) : false;

                setCommerceStatus(newStatus ? newStatus : null);
            })
            setRefreshing(false);
        }).catch(() => {
            if (onError) {
                onError();
            }
        })
        getOrderPayments();
    }, [orderCode, onError, getOrderPayments]);

    useEffect(() => {
        getOrder();
    }, [orderCode, getOrder]);

    const copyPaymentUrl = () => {
        paymentUrlInput.current.select();
        document.execCommand('copy');

        setUrlCopied(true);

        setTimeout(function () {
            setUrlCopied(false);
        }, 2000);
    }

    const updateCommerceStatus = () => {
        setUpdatingCommerceStatus(true);
        orderServices.updateProductStatus(order.business_order_code, commerceStatus.value).then(() => {
            setOrder({
                ...order,
                product_order_status: commerceStatus.value,
            });
        }).catch(() => {
            let revertStatus = order.product_order_status ? commerceStatusCodes.find(code => code.value === order.product_order_status.toString()) : false;
            setCommerceStatus(revertStatus ? revertStatus : null);
        }).finally(() => {
            setUpdatingCommerceStatus(false);
        });
    }

    return (
        <div className={'section payment-summary loader-container' + (className ? (' ' + className) : '')}>
            <Loader loading={!order}/>
            {order &&
            <React.Fragment>
                <div className="portlet summary-summary">
                    <div className="portlet-header gap-bottom">
                        <div className="portlet-infobar">
                            <div className="infobar-section">
                                <strong className="section-title">Ödeme Tutarı</strong>
                                <span className="section-data">
										<Pricebox
                                            currency={order.price_type.currency_symbol}
                                            price={order.total_amount}/>
                                    {(order.currency_exchange_amount && order.currency_exchange_amount > 0) &&
                                    <p>
												<span className="label text-regular">
													<Pricebox
                                                        monochrome
                                                        price={order.currency_exchange_amount}/>
												</span>
                                    </p>
                                    }
									</span>
                            </div>
                            <div className="infobar-section">
                                <strong className="section-title">Müşteri</strong>
                                <span className="section-data">
										{order.client.company_name} {(order.client.company_name && (order.client.executive_name || order.client.executive_surname)) ? '-' : ''} {order.client.executive_name} {order.client.executive_surname}
									</span>
                            </div>
                        </div>

                        {(order.order_plan_id !== null && order.is_product_order === false) &&
                        <div className="portlet-controls">
                            <Btn
                                icon="edit"
                                onClick={() => {
                                    openModal('update-order', {orderCode: order.business_order_code});
                                }}
                                low gray outline>
                                ödemeyi düzenle
                            </Btn>
                        </div>
                        }
                    </div>
                    <div className="portlet-section top">
                        <div className="portlet-infobar">
                            <div className="grid-container">
                                <div className="row">
                                    <div className="col x3 m-x6">
                                        <div className="infobar-section">
                                            <strong className="section-title">Ödenen Tutar</strong>
                                            <span className="section-data">
													<Pricebox
                                                        currency={order.price_type.currency_symbol}
                                                        price={order.paid_price}/>
												</span>
                                        </div>
                                    </div>
                                    <div className="col x3 m-x6">
                                        <div className="infobar-section">
                                            <strong className="section-title">Kalan Tutar</strong>
                                            <span className="section-data">
													<Pricebox
                                                        currency={order.price_type.currency_symbol}
                                                        price={order.remaining_price}/>
												</span>
                                        </div>
                                    </div>
                                    {order.due_date &&
                                    <>
                                        <div className="col x3 m-x6">
                                            <div className="infobar-section">
                                                <strong className="section-title">Oluşturulma Tarihi</strong>
                                                <span className="section-data">
															{formatDate(order.created_at)}
														</span>
                                            </div>
                                        </div>
                                        <div className="col x3 m-x6">
                                            <div className="infobar-section">
                                                <strong className="section-title">Ödeme Vadesi</strong>
                                                <span className="section-data">
															{formatDate(order.due_date)}
														</span>
                                            </div>
                                        </div>
                                    </>
                                    }
                                </div>
                            </div>
                        </div>
                    </div>
                    <div
                        className={`portlet-section bottom summary-status status-${order.statusObject ? order.statusObject.status : 'default'}`}>
                        <div className="status-text">
                            <p className="text-title">Ödeme Durumu</p>
                            <OrderStatus label status={order.status}/>
                            {/* <label className="label yellow">{order.status_name}</label> */}
                        </div>

                        <Btn
                            icon="refresh"
                            loading={refreshing}
                            onClick={getOrder}
                            plain>
                            güncelle
                        </Btn>
                    </div>
                </div>

                {mode === 'detail' && order.is_product_order &&
                <div className="portlet">
                    <div className="portlet-header">
                        <strong className="portlet-title">
                            Sipariş Bilgileri
                        </strong>
                    </div>
                    <div className={`portlet-section grid-container${order.addressDelivery === null ? ' bottom' : ''}`}>
                        <div className="row">
                            <div className="col flex">
                                <FormInput
                                    disabled={updatingCommerceStatus}
                                    type="select"
                                    label="Sipariş Durumu"
                                    onChange={setCommerceStatus}
                                    options={commerceStatusCodes}
                                    value={commerceStatus}/>
                            </div>
                            <div className="col free">
                                <Btn
                                    big blue
                                    onClick={updateCommerceStatus}
                                    loading={updatingCommerceStatus}
                                    disabled={
                                        updatingCommerceStatus ||
                                        (!order.product_order_status && !commerceStatus) ||
                                        (commerceStatus && order.product_order_status && order.product_order_status.toString() === commerceStatus.value)
                                    }>
                                    Güncelle
                                </Btn>
                            </div>
                        </div>
                    </div>

                    {order.addressDelivery !== null &&
                    <div className="portlet-section border-top bottom">
                        <div className="portlet-infobar">
                            <div className="grid-container">
                                <div className="row">
                                    <div className="col">
                                        <div className="infobar-section">
                                            <strong className="section-title">Teslimat Yöntemi</strong>
                                            <span className="section-data">
															{order.addressDelivery ? 'Adrese Teslimat' : <>Mağazada
                                                                Teslimat: <strong>{order.target_group.name}</strong></>}
														</span>
                                        </div>
                                    </div>
                                </div>
                                {order.addressDelivery &&
                                <div className="row">
                                    <div className="col">
                                        <div className="infobar-section">
                                            <strong className="section-title">Teslimat Adresi</strong>
                                            <span className="section-data">
																<p>(<strong>{order.client.delivery_title}</strong>) {order.client.delivery_address} - {order.client.delivery_district}, {order.client.delivery_city}
																</p>
																<p>
																	{order.client.delivery_owner}, {order.client.delivery_phone}
																</p>
															</span>
                                        </div>
                                    </div>
                                </div>
                                }
                            </div>
                        </div>
                    </div>
                    }
                </div>
                }

                <div className="portlet">
                    <Tabs>
                        <div
                            index="payment"
                            icon="link"
                            label="Ödeme Linki">
                            <div className="summary-links">

                                {!location.pathname.includes('terminal') && (
                                    <div className="links-url">
                                        <input
                                            ref={paymentUrlInput}
                                            type="text"
                                            className="url-input"
                                            value={order.order_url}
                                            readOnly/>
                                        <Btn
                                            className="url-cta copy"
                                            success={urlCopied ? 'true' : undefined}
                                            onClick={copyPaymentUrl}
                                            icon="copy">
                                            {urlCopied ? 'Kopyalandı!' : 'Linki Kopyala'}
                                        </Btn>
                                        <Btn
                                            tag="a"
                                            target="_blank"
                                            rel="noopener"
                                            href={order.order_url}
                                            className="url-cta link big-icon-pre"
                                            blue
                                            icon="card">
                                            Ödeme Ekranı
                                        </Btn>
                                    </div>
                                )}

                                <div className="links-qr">
                                    <img
                                        className="qr-image" src={order.qrImage}
                                        alt={order.order_url}
                                    />

                                    <div className="qr-info">
                                        <strong className="info-title">QR Link</strong>
                                        <p>QR kodu kameradan okuttuğunuzda ödeme linkine yönlendirilrsiniz</p>
                                    </div>
                                </div>

                                <div className="links-buttons">
                                    <strong className="buttons-title">Linki Gönder</strong>

                                    <div>
                                        <Btn
                                            className="summary-sendlink-email"
                                            icon="email"
                                            onClick={() => {
                                                openModal('order-payment-link', {
                                                    mode: 'email',
                                                    top: true,
                                                    order: order,
                                                    orderCode: orderCode,
                                                    onSuccess: () => {
                                                        if (onNotificationSent) {
                                                            onNotificationSent();
                                                        }
                                                    }
                                                });
                                            }}
                                            blue wide>
                                            E-Posta
                                        </Btn>
                                        <Btn
                                            className="summary-sendlink-sms"
                                            icon="sms"
                                            onClick={() => {
                                                openModal('order-payment-link', {
                                                    mode: 'sms',
                                                    top: true,
                                                    order: order,
                                                    orderCode: orderCode,
                                                    onSuccess: () => {
                                                        if (onNotificationSent) {
                                                            onNotificationSent();
                                                        }
                                                    }
                                                });
                                            }}
                                            blue wide>
                                            SMS
                                        </Btn>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div
                            index="past"
                            icon="cards"
                            onChange={getOrderPayments}
                            label="Ödeme Geçmişi">
                            <div className="summary-history loader-container">
                                <Loader loading={!orderPayments}/>
                                {orderPayments &&
                                <Table
                                    emptyMessage="Bu sipariş altında ödeme bulunmuyor."
                                    className="history-list"
                                    heading={
                                        <React.Fragment>
                                            <th>Oluşturulma Tarihi</th>
                                            <th>Kart No</th>
                                            <th>Ödeme Türü</th>
                                            <th>Ödenen</th>
                                            <th>Taksit Sayısı</th>
                                        </React.Fragment>
                                    }>
                                    {orderPayments.map((payment, nth) => (
                                        <TR key={nth} mobileTitle={nth + 1 + '. Ödeme'}>
                                            <TD>
                                                <p>
                                                    {formatDate(payment.created_at, 'dd MMMM yyyy')}
                                                </p>
                                                <p className="table-subtitle">
                                                    {formatDate(payment.created_at, 'HH:mm')}
                                                </p>
                                            </TD>
                                            <TD>
                                                <p>{payment.card_masked}</p>
                                                <p className="table-subtitle">
                                                    {payment.card_name}
                                                </p>
                                                <p className="table-subtitle">
                                                    {payment.approve_code}
                                                </p>
                                            </TD>
                                            <TD>
                                                <p>{payment.payment_name}</p>
                                                {payment.is_3d &&
                                                <span className="label success">3D</span>
                                                }
                                            </TD>
                                            <TD>
                                                <p>
                                                    <Pricebox
                                                        currency={payment.order.price_type.currency_symbol}
                                                        price={payment.real_price}/>
                                                </p>
                                                {payment.currency_exchange_total > 0 ?
                                                    <React.Fragment>
                                                        <p>
																	<span className="label text-regular">
																		<Pricebox
                                                                            monochrome
                                                                            price={payment.currency_exchange_total}/>
																	</span>
                                                        </p>
                                                        <p>
                                                            <ConversionInfo
                                                                exchangeType={payment.currency_exchange_rate.currency_exchange_type.id}
                                                                currency={getCurrencySymbol(payment.currency_exchange_rate.currency_code, false, true)}
                                                                price={payment.currency_exchange_rate.currency_exchange_rate}/>
                                                        </p>

                                                        {payment.currency_exchange_refund_total > 0 &&
                                                        <p>
																		<span className="label error">
																			İade : <Pricebox monochrome
                                                                                             currency={payment.order.price_type.currency_symbol}
                                                                                             price={payment.currency_exchange_refund_total}/>
																		</span>
                                                        </p>
                                                        }
                                                    </React.Fragment>
                                                    :
                                                    <React.Fragment>
                                                        {payment.real_price !== payment.price &&
                                                        <p>
																		<span className="label warning">
																			Çekilen :
																			<Pricebox
                                                                                monochrome
                                                                                price={payment.real_price}/>
																		</span>
                                                        </p>
                                                        }
                                                        {payment.refund_total > 0 &&
                                                        <p>
																		<span className="label error">
																		İade : <Pricebox monochrome
                                                                                         price={payment.refund_total}/>
																		</span>
                                                        </p>
                                                        }
                                                    </React.Fragment>
                                                }
                                            </TD>
                                            <TD>
                                                <p className="table-subtitle">{payment.installment_count} Taksit</p>
                                            </TD>
                                        </TR>
                                    ))}
                                </Table>
                                }
                            </div>
                        </div>
                        <div
                            index="notification"
                            icon="info"
                            label="Bildirim Geçmişi">
                            <div className="summary-notifications">
                                <Table emptyMessage="Bildirim geçmişi yok">
                                    {order.notification_history.map((log, nth) => (
                                        <TR key={nth}>
                                            <TD className="mobile-full">
                                                <p>
                                                    <strong>{notificationTypes[log.method]}</strong> ({log.type === 'sms' ? 'SMS' : 'E-Posta'})
                                                </p>
                                                <p>{log.contact}</p>
                                            </TD>
                                            <TD>
                                                <p>
                                                    {formatDate(log.created_at, 'dd MMMM yyyy')}
                                                </p>
                                                <p className="table-subtitle">
                                                    {formatDate(log.created_at, 'HH:mm')}
                                                </p>
                                            </TD>
                                        </TR>
                                    ))}
                                </Table>
                            </div>
                        </div>
                    </Tabs>
                </div>

                {(order && order.parsedProducts && (order.parsedProducts.active.length > 0 || order.parsedProducts.returned.length > 0)) &&
                <section className="portlet section detail-products">
                    <div className="portlet-header">
                        <strong className="portlet-title">
                            Ürünler
                        </strong>
                    </div>

                    <div className="portlet-section">
                        <Table
                            emptyMessage="Hiç ürün bulunmuyor."
                            className="products-table"
                            heading={
                                <React.Fragment>
                                    <th>Görsel</th>
                                    <th>Ürün</th>
                                    <th>Fiyat</th>
                                </React.Fragment>
                            }>
                            {order.parsedProducts.active.map((product, nth) => (
                                <TR key={nth} className="table-product">
                                    <TD className="product-image">
                                        <div className="table-imagewrap">
                                            <Image src={product.product.image_link}/>
                                        </div>
                                    </TD>
                                    <TD className="product-description">
                                        <strong>
                                            {product.product.title}
                                        </strong>
                                        <p><strong>{product.quantity} adet</strong> x <Pricebox monochrome
                                                                                                price={product.price_new}/>
                                        </p>
                                        <p className="table-subtitle">
                                            {product.product.mpn}
                                        </p>
                                        {order.addressDelivery &&
                                        <p className="table-subtitle">
                                            {product.product.cargo_day} gün içinde kargoya verilir.
                                        </p>
                                        }
                                    </TD>
                                    <TD className="product-price">
                                        <Pricebox price={product.total_price}/>
                                    </TD>
                                </TR>
                            ))}
                            {order.parsedProducts.returned.map((product, nth) => (
                                <TR key={nth} className="table-product">
                                    <TD className="product-image">
                                        <div className="table-imagewrap">
                                            <Image src={product.product.image_link}/>
                                        </div>
                                    </TD>
                                    <TD className="product-description">
                                        <strong>
                                            {product.product.title}
                                        </strong>
                                        <p><strong>{product.quantity} adet</strong></p>
                                        <p>
                                            <span className="label warning">İade Edildi</span>
                                        </p>
                                        <p className="table-subtitle">
                                            {product.product.product_id}
                                        </p>
                                    </TD>
                                    <TD className="product-price">
                                        <Pricebox price={product.price_new * product.quantity}/>
                                    </TD>
                                </TR>
                            ))}
                        </Table>
                    </div>
                </section>
                }
            </React.Fragment>
            }
        </div>
    )
}

const ConnectedModal = connect(mapStateToProps)(OrderSummary);

ConnectedModal.defaultProps = {
    mode: 'link',
}

export default ConnectedModal
